import InputMask from 'react-input-mask';
import { Button, Checkbox, DatePicker, Input, Modal, Popconfirm, Radio, Select, Spin, Table, TableProps, Upload } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { Role } from '@shared/utils/enums';
import { updateUserRequest } from '@shared/services/user.service';
import { IDocument } from '@interfaces/document.interface';
import { MdOutlineIosShare, MdOutlineNoteAdd } from 'react-icons/md';
import { FiDownloadCloud } from 'react-icons/fi';
import { DeleteOutlined } from '@ant-design/icons';
import { formatFileSize, uploadFile } from '@shared/utils/functions';
import { createDocumentRequest, deleteDocumentRequest } from '@shared/services/document.service';

const UserModal = ({ open, offices, referrers, setOpen, user, messageApi, onUpdate }: any) => {
	const [loading, setLoading] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [formData, setFormData] = useState<any>(null);
  const [documents, setDocuments] = useState<IDocument[]>([]);

	const customFormat = (value: dayjs.Dayjs) => `${value.format('MM/DD/YYYY')}`;

	useEffect(() => {
		if(!user) return;

		setFormData({ ...user });
		setDocuments([...user.documents]);
	}, [user]);

	const _onUpdatePress = () => {
		setLoading(true);
		const values = { ...formData };

		delete values.__typename;
		delete values.documents;

		updateUserRequest({ ...values, role: values?.role ? values?.role.toUpperCase() : null })
			.then(() => {
				messageApi.open({ type: 'success', content: 'Profile information updated correctly' });
				onUpdate(formData);
				setOpen(false);
				setLoading(false);
			})
			.catch(() => {
				setLoading(false);
				messageApi.open({ type: 'error', content: 'Failed to update information' });
			});
	};

	const _onCancel = () => {
		setOpen(false);
	};

	const _onChange = (name: string, value: any) => {
    setFormData({ ...formData, [name]: value });
  };

	const _UploadDocument = async ({ file }: any) => {
		setUploading(true);
		const upload = await uploadFile(file, 'file', 'document');

		createDocumentRequest({ ownerId: formData.id, url: upload.url, modifyDate: file.lastModifiedDate, name: file.name.slice(0, file.name.lastIndexOf('.')), extension: file.name.slice(file.name.lastIndexOf('.') + 1), size: file.size })
			.then(async (res) => { 
				setDocuments([ ...documents, res.response ]);
				setFormData({ ...formData, documents: [ ...documents, res.response ] });
				setUploading(false);
				messageApi.open({ type: 'success', content: 'Document uploaded correctly' });
			});
	};
	
	const _DeleteDocument = async (id: number) => {
		const cloned = [...documents];
		const index = cloned.findIndex((item) => id === item.id);

		if (index > -1) {
			cloned.splice(index, 1);
			setDocuments([...cloned]);
			setFormData({ ...formData, documents: [...cloned] });
		}

		await deleteDocumentRequest(id);
		messageApi.open({ type: 'success', content: 'Document deleted correctly' });
	};

	const share = async (url: string) => {
    await navigator.clipboard.writeText(url);
    messageApi.open({ type: 'success', content: 'Download link copied to clipboard' });
  };

	const columns: TableProps<IDocument>['columns'] = [
		{ title: <span className='text-xs'>File Name</span>, width: 280, dataIndex: 'name', key: 'name', render: (_, record) => `${record.name}.${record.extension}`},
		{ title: <span className='text-xs'>Size</span>, width: 80, dataIndex: 'size', key: 'size', render: (value) => formatFileSize(value)},
		{ title: <span className='text-xs'>Upload Date</span>, width: 120, dataIndex: 'createdAt', key: 'createdAt', render: (date) => date ? dayjs(date).format('MM/DD/YYYY') : '-' },
		{
			title: <div className='flex items-center justify-center'><span className='text-xs mr-2 ml-2'>Actions</span>{ uploading ? <Spin /> : 
			<Upload className='cursor-pointer' showUploadList={false} customRequest={_UploadDocument}>
				<MdOutlineNoteAdd title='Add Document' className='mt-1' size={19}/>
			</Upload>}
			</div>, width: 80,
			fixed: 'right',
			dataIndex: 'operation',
			render: (_: any, record: any) => {
					return <span className='flex flex-row'>
						<MdOutlineIosShare onClick={() => share(record.url)} className='ml-4 mr-4 cursor-pointer' title='Share'/>
						<a className='hover:no-underline hover:text-inherit' href={record.url} download='Download' target='_blank' rel='noreferrer'>
							<FiDownloadCloud className='mr-4 cursor-pointer' title='Download'/>
						</a>
						<Popconfirm title={`Delete this Document?`} icon={null} okText='Yes' cancelText='No' onConfirm={() => _DeleteDocument(record.id)}>
							<DeleteOutlined title='Delete' className='mr-4'/>
						</Popconfirm>
					</span>
			},
		}
	];

	return (
		<Modal
			open={open}
			title='Update User'
			width={470}
			destroyOnClose
			onCancel={_onCancel}
			footer={[
				<Button key='back' onClick={_onCancel}>Cancel</Button>,
				<Button key='submit' type='primary' loading={loading} onClick={_onUpdatePress}>Update</Button>,
			]}
		>
			<div className='w-full flex flex-col mb-4'>
				<div className='w-full grid grid-cols-9 gap-4'>
					<div className='col-span-6'>
						<div className='text-xs'>Full Name</div>
						<Input 
							className='w-full' 
							placeholder='Full Name cannot be empty' 
							status={formData?.fullName ? '' : 'error'} 
							value={formData?.fullName} 
							onChange={(e) => _onChange('fullName', e.target.value)}
						/>
					</div>
					<div className='col-span-3'>
						<div className='text-xs'>Role</div>
						<Select
							showSearch
							placeholder='Select a role'
							optionFilterProp='label'
							className='w-full'
							options={Object.values(Role).map((val) => ({ value: val, label: val }))}
							value={formData?.role ? formData?.role.charAt(0).toUpperCase() + formData?.role.slice(1).toLowerCase() : null}
							onChange={(e) => _onChange('role', e)}
						/>
					</div>
					<div className='col-span-3'>
						<div className='text-xs'>Hire Date</div>
						<DatePicker 
							format={customFormat}
							className='w-full' 
							value={formData?.hireDate ? dayjs(formData?.hireDate) : null}
							onChange={(e) => _onChange('hireDate', e ? e.toDate() : null)}
						/>
					</div>
					<div className='col-span-6'>
						<div className='text-xs'>Office</div>
						<Select
							showSearch
							placeholder='Select an office'
							optionFilterProp='label'
							className='w-full'
							options={offices}
							value={formData?.officeId}
							onChange={(e) => _onChange('officeId', e)}
						/>
					</div>
					<div className='col-span-3'>
						<div className='text-xs'>Birth Date</div>
						<DatePicker 
							className='w-full' 
							format={customFormat}
							value={formData?.birthDate ? dayjs(formData?.birthDate) : null}
							onChange={(e) => _onChange('birthDate', e ? e.toDate() : null)}
						/>
					</div>
					<div className='col-span-3'>
						<div className='text-xs'>{formData?.personal ? 'SSN' : 'EIN'}</div>
						{formData?.personal ? 
							<InputMask 
								mask='999-99-9999' 
								maskChar='_'
								max={8}
								value={formData?.ssn} 
								onChange={(e) => _onChange('ssn', e.target.value.replace(/\D/g, '').slice(0, 9))}
							>
								{(inputProps) => <Input {...inputProps} className='w-full' />}
							</InputMask>
							: 
							<InputMask 
								mask='99-9999999'
								maskChar='_'
								value={formData?.ssn} 
								onChange={(e) => _onChange('ssn', e.target.value.replace(/\D/g, '').slice(0, 9))}
							>
								{(inputProps) => <Input {...inputProps} className='w-full' />}
							</InputMask>
						}
					</div>
					<div className='col-span-3 flex items-center'>
						<Radio.Group 
							className='ssn-radio-modal mt-4 flex' 
							block 
							options={[{ label: 'Personal', value: 'Personal' }, { label: 'Business', value: 'Business' }]} 
							onChange={(e)  => _onChange('personal', e.target.value === 'Personal') }
							value={formData?.personal ? 'Personal' : 'Business'}
							optionType='button' 
						/>
					</div>
					<div className='col-span-3'>
						<div className='text-xs'>Primary Phone</div>
						<InputMask 
							mask='(999) 999 9999'
							maskChar='_'
							value={formData?.phone}
							onChange={(e: any) => _onChange('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
						>
							{(inputProps) => <Input {...inputProps} className='w-full' />}
						</InputMask>
					</div>
					<div className='col-span-3'>
						<div className='text-xs'>Secondary Phone</div>
						<InputMask 
							mask='(999) 999 9999'
							maskChar='_'
							value={formData?.fax}
							onChange={(e: any) => _onChange('fax', e.target.value.replace(/\D/g, '').slice(0, 10))}
						>
							{(inputProps) => <Input {...inputProps} className='w-full' />}
						</InputMask>
					</div>
					<div className='col-span-3'>
						<div className='text-xs'>Emergency Phone</div>
						<InputMask 
							mask='(999) 999 9999'
							maskChar='_'
							value={formData?.emergency}
							onChange={(e: any) => _onChange('emergency', e.target.value.replace(/\D/g, '').slice(0, 10))}
						>
							{(inputProps) => <Input {...inputProps} className='w-full' />}
						</InputMask>
					</div>
					<div className='col-span-7'>
						<div className='text-xs'>Email Address</div>
						<Input 
							className='w-full' 
							placeholder='Email cannot be empty' 
							status={formData?.email ? '' : 'error'} 
							value={formData?.email}
							onChange={(e) => _onChange('email', e.target.value)}
						/>
					</div>
					<div className='col-span-2 row-span-2'>
						<Checkbox
							className='text-xs mt-6'	
							checked={formData?.active}
							onChange={(e) => _onChange('active', e.target.checked)}
						>
							Active
						</Checkbox>
						<Checkbox
							className='text-xs mt-4'	
							checked={formData?.admin}
							onChange={(e) => _onChange('admin', e.target.checked)}
						>
							Admin
						</Checkbox>
						<Checkbox
							className='text-xs mt-4'	
							checked={formData?.invited}
							onChange={(e) => _onChange('invited', e.target.checked)}
						>
							Invited
						</Checkbox>
					</div>
					<div className='col-span-4'>
						<div className='text-xs'>Referred By</div>
						<Select
							showSearch
							placeholder='Select a user'
							optionFilterProp='label'
							className='w-full'
							options={referrers}
							value={formData?.referrerId}
							onChange={(e) => _onChange('referrerId', e)}
						/>
					</div>
					<div className='col-span-3'>
						<div className='text-xs'>Referred Date</div>
						<DatePicker 
							className='w-full' 
							format={customFormat}
							value={formData?.referredDate ? dayjs(formData?.referredDate) : null}
							onChange={(e) => _onChange('referredDate', e ? e.toDate() : null)}
						/>
					</div>
					<div className='col-span-2'></div>
				</div>
				<div className='mt-4'>
						<div className='text-xs'>Documents</div>
            <Table<IDocument>
              columns={columns}
              size='small'
              pagination={false}
              dataSource={documents}
              scroll={{ x: 'max-content', y: 150 }}
            />
					</div>
			</div>
		</Modal>
	);
}

export default UserModal;